import { useState, useEffect } from 'react';

import { Trans } from '@lingui/macro';
import { useDebounce } from 'usehooks-ts';

import useFetchGmapsPlace from '../../../hooks/useFetchGmapsPlace';
import { adaptPlaceForBackend, containsLatLng } from '../../../utils/mapsUtil';
import { isStrNullOrEmpty } from '../../../utils/strUtil';
import Select from './Select';

export const LOCATION_FILTER_NAME = 'location';

const SEARCH_UPDATE_DELAY_MS = 400;
const MIN_CHAR_TRIGGER_SEARCH = 2;

export default function SelectPlace({ fieldValue, onChange: onFilterChange, ...props }) {
  const { isPlaceServiceLoaded, getLatLngFromName, getNameFromLatLng, getPlaceFromSuggestion, getPlacesPrediction } = useFetchGmapsPlace();
  const [resultsLoading, setResultsLoading] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const debouncedValue = useDebounce(searchValue, SEARCH_UPDATE_DELAY_MS);
  const [searchChoices, setSearchChoices] = useState([]);

  const [saveFilterProcessed, setSavedFilterProcessed] = useState(false);
  const [selectFirstItem, setSelectFirstItem] = useState(false);

  // process the pre existing filters, either storage saved or url given
  useEffect(() => {
    if (saveFilterProcessed) return;

    // no value to process, no need to wait for maps service
    if (isStrNullOrEmpty(fieldValue)) {
      setSavedFilterProcessed(true);
      return;
    }

    if (!isPlaceServiceLoaded.current) return;

    if (!containsLatLng(fieldValue)) {
      doProcessSavedNameFilter(fieldValue);
      return;
    }

    doProcessSavedLatLngFilter(fieldValue);
  }, [isPlaceServiceLoaded.current]);

  const doProcessSavedNameFilter = placeName => {
    getLatLngFromName(placeName, latLngValue => {
      const newSelectedItem = {
        label: placeName,
        value: latLngValue,
      };
      onPlaceSelected(newSelectedItem, true);
    });
  };

  const doProcessSavedLatLngFilter = latLngValue => {
    getNameFromLatLng(latLngValue, placeName => {
      const newSelectedItem = {
        label: placeName,
        value: latLngValue,
      };
      onPlaceSelected(newSelectedItem, true);
    });
  };

  // Trigger search on input change
  useEffect(() => {
    if (!debouncedValue || debouncedValue.length < MIN_CHAR_TRIGGER_SEARCH) return;

    setResultsLoading(true);
    getPlacesPrediction(debouncedValue).then(results => {
      setSearchChoices(results);
      setResultsLoading(false);
    });
  }, [debouncedValue, getPlacesPrediction]);

  // item clicked in the select
  const onSelectedItem = (option, setSelectedOption) => {
    setSelectedOption(option);

    if (option == null) {
      onPlaceSelected(null);
      return;
    }

    // re applying a saved filter without calling maps api
    if (option.suggestion == null && option.value != null) {
      onFilterChange(props.fieldName, option.value);
      return;
    }

    getPlaceFromSuggestion(option.suggestion, onPlaceSelected);
  };

  // Triggers the filter change = the page list load
  const onPlaceSelected = (place, isProcessingSavedFilters) => {
    if (place == null) {
      onFilterChange(props.fieldName, '');
      return;
    }

    if (isProcessingSavedFilters) {
      setSearchChoices([place]);
      setSelectFirstItem(true);
      setSavedFilterProcessed(true);
      onFilterChange(props.fieldName, place.value);
      return;
    }

    const latLngValue = adaptPlaceForBackend(place);
    onFilterChange(props.fieldName, latLngValue);
  };

  return (
    <Select
      {...props}
      fieldValue={searchValue}
      fieldChoices={searchChoices}
      selectFirstChoice={selectFirstItem}
      onInputChange={val => setSearchValue(val)}
      handleOnChangeOverride={onSelectedItem}
      isClearable
      isSearchable
      isLoading={resultsLoading}
      isDisabled={!saveFilterProcessed}
      fieldPlaceholder={<Trans>Activity city</Trans>}
    />
  );
}
